import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Button, CircularProgress, IconButton, InputAdornment, TextField } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import CountdownRing, { formatCountdown, URGENT_SECONDS } from "./CountdownRing";
import { readLastActivity } from "./idleSessionStore";
import styles from "./IdleSession.module.css";

const initialsOf = (name, email) => {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : source.slice(0, 2);
  return letters.toUpperCase();
};

const timeLabel = (date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// Full-screen lock over the still-mounted app (see IdleSessionGuard.js).
// The username is the signed-in account's and can't be changed here - to
// switch user, sign out.
const IdleLockScreen = ({
  user,
  businessName,
  lockMinutes,
  secondsLeft,
  warnSeconds,
  attemptsLeft,
  onUnlock,
  onSignOut,
  // SuperAdmin "test lock screen": same UI, but nothing is really locked and
  // "Sign out" just closes the preview.
  preview = false,
  onClosePreview,
  // Real lock, but triggered by SuperAdmin test mode (5 s idle).
  testMode = false,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState("");
  // Read-only until focused so browsers can't auto-fill a saved password.
  const [pwReady, setPwReady] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const passwordRef = useRef(null);
  const cardRef = useRef(null);
  const mountedRef = useRef(true);

  // When the idle clock says it locked - not when this component mounted,
  // which after a reload would be "just now".
  const lockedAt = useMemo(() => {
    const last = readLastActivity();
    const at = last ? last + lockMinutes * 60000 : Date.now();
    return new Date(Math.min(at, Date.now()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const interval = setInterval(() => setNow(new Date()), 1000);
    // Focus after the overlay is painted and the app behind it is inert.
    const focusTimer = setTimeout(() => passwordRef.current?.focus(), 60);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearTimeout(focusTimer);
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!password || busy || signingOut) return;
    setBusy(true);
    setError("");
    const result = await onUnlock(password);
    // A successful unlock (or a sign-out after too many attempts) unmounts us.
    if (!mountedRef.current) return;
    setBusy(false);
    if (!result?.ok) {
      setError(result?.message || "Incorrect password");
      // Replays on every failure, unlike a CSS class that only fires once.
      if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        cardRef.current?.animate?.(
          [0, -10, 9, -7, 5, -2, 0].map((x) => ({ transform: `translateX(${x}px)` })),
          { duration: 420, easing: "ease-out" }
        );
      }
      setPassword("");
      passwordRef.current?.focus();
    }
  };

  const handleSignOut = () => {
    if (signingOut) return;
    setSigningOut(true);
    onSignOut();
  };

  const displayName = user?.name || user?.email || "Signed-in user";
  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const counting = secondsLeft !== null && secondsLeft !== undefined;
  const urgent = counting && secondsLeft <= URGENT_SECONDS;

  return ReactDOM.createPortal(
    <div
      className={styles.lockOverlay}
      data-idle-overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-lock-title"
    >
      <div className={styles.lockGlowA} />
      <div className={styles.lockGlowB} />

      <div className={styles.lockClock} aria-hidden="true">
        <div className={styles.clockTime}>{timeLabel(now)}</div>
        <div className={styles.clockDate}>
          {now.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
        </div>
      </div>

      <form
        ref={cardRef}
        className={styles.lockCard}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        {(preview || testMode) && (
          <div className={styles.lockAttempts} style={{ marginBottom: 8, fontWeight: 700 }}>
            {testMode ? "TEST MODE - locks after 5 seconds idle" : "TEST PREVIEW - Sign out really ends this session"}
          </div>
        )}
        <div className={styles.avatarWrap}>
          <div className={styles.avatar}>{initialsOf(user?.name, user?.email)}</div>
          <span className={styles.avatarBadge}>
            <LockOutlinedIcon style={{ fontSize: 15 }} />
          </span>
        </div>

        <h2 id="idle-lock-title" className={styles.lockName}>
          {displayName}
        </h2>
        <div className={styles.lockedChip}>
          <span className={styles.lockedDot} />
          Screen locked at {timeLabel(lockedAt)}
        </div>
        <p className={styles.lockHint}>
          Locked after {testMode ? "5 seconds" : `${lockMinutes} minute${lockMinutes === 1 ? "" : "s"}`} of inactivity. Enter your
          password to pick up right where you left off.
        </p>

        {counting && (
          <div className={`${styles.countdownStrip} ${urgent ? styles.countdownStripUrgent : ""}`}>
            <CountdownRing
              secondsLeft={secondsLeft}
              totalSeconds={warnSeconds}
              size={44}
              stroke={5}
              showLabel={false}
            />
            <div className={styles.countdownText}>
              <strong>Auto sign-out in {formatCountdown(secondsLeft)}</strong>
              <span>Unlock now to keep this session and your open work.</span>
            </div>
          </div>
        )}

        <TextField
          label="Username"
          value={user?.email || ""}
          variant="outlined"
          size="small"
          fullWidth
          className={styles.lockField}
          helperText="Signed-in account - can't be changed here"
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineOutlinedIcon fontSize="small" style={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <LockOutlinedIcon fontSize="small" style={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          // Not "username": stops browsers/password managers treating this
          // as a login form and auto-filling the saved password.
          inputProps={{ autoComplete: "off", name: "locked-account", tabIndex: -1, "aria-readonly": true }}
        />

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError("");
          }}
          variant="outlined"
          size="small"
          fullWidth
          inputRef={passwordRef}
          error={Boolean(error)}
          disabled={busy || signingOut}
          className={styles.lockField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <VpnKeyOutlinedIcon fontSize="small" style={{ color: error ? "#dc2626" : "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  edge="end"
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            autoComplete: "new-password",
            name: "unlock-secret",
            readOnly: !pwReady,
            onFocus: () => setPwReady(true),
          }}
        />

        {error ? (
          <div className={styles.lockError} role="alert">
            <ErrorOutlineOutlinedIcon style={{ fontSize: 16 }} />
            <span>{error}</span>
          </div>
        ) : (
          attemptsLeft < 5 && (
            <div className={styles.lockAttempts}>
              {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} left before automatic sign-out
            </div>
          )
        )}

        <Button
          type="submit"
          className={styles.unlockBtn}
          disableElevation
          fullWidth
          disabled={!password || busy || signingOut}
        >
          {busy ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Unlock"}
        </Button>

        <div className={styles.orDivider}>
          <span>or</span>
        </div>

        <Button
          className={styles.signOutBtn}
          fullWidth
          onClick={handleSignOut}
          disabled={busy || signingOut}
          startIcon={
            signingOut ? <CircularProgress size={16} /> : <ExitToAppOutlinedIcon style={{ fontSize: 18 }} />
          }
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </Button>
        {preview && (
          <Button fullWidth size="small" onClick={onClosePreview} disabled={signingOut} style={{ marginTop: 6, textTransform: "none", color: "#64748b" }}>
            Close preview (stay signed in)
          </Button>
        )}
        <p className={styles.switchHint}>
          {firstName ? `Not ${firstName}? ` : ""}Sign out so someone else can log in.
        </p>
      </form>

      <div className={styles.lockFooter}>
        {businessName ? <strong>{businessName}</strong> : null}
        {businessName ? " · " : ""}Your open screen and cart are kept while locked
      </div>
    </div>,
    document.body
  );
};

export default IdleLockScreen;
